import { getImageCode, getSMSCode, postAuthorizeRegister } from "@/apis/common";
import { checkDevice, loginDevice } from "@/apis/user";
import { useConfigContext } from "@/app";
import {
  LINK_ACCOUNT_TOKEN,
  LOGIN_REMEMBER_PASSWORD,
  LOGIN_USER_ACCOUNT_INFO,
  USER_TOKEN,
} from "@/constants";
import { KEY_FOR_ACCOUNT, SALT_FOR_ACCOUNT } from "@/constants/key";
import {
  validateImageCode,
  validatePassword,
  validatePhoneNumber,
  validateSMSCode,
  validateUserName,
} from "@/libs";
import { StateActions, useCreateReducer } from "@/libs/reducer";
import { insertDx } from "@/logic/index";
import { useGlobalVerify } from "@/logic/verify";
import { useUserAuthContext } from "@/routes/routesAuths/userAuth";
import { selectorLocalStorage, useLocalStorage } from "@/stores";
import { UserInfo } from "@/types/Api/User/MyAccount/userInfo";
import { APIUserMyAccountLogin, LoginDeviceParams } from "@/types/Api/User/User/MyAccount/login";
import { APIUserMyAccountLoginSMS } from "@/types/Api/User/User/MyAccount/loginSMS";
import { RequestResponse, setBusinessAxiosToken } from "@/utils/request";
import { Toast } from "antd-mobile";
import { ToastHandler } from "antd-mobile/es/components/toast";
import CryptoJS from "crypto-js";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { login, loginSMS } from "./../../apis/user";

/* `decodeAccountAndPassword`
  是一个函数，它从本地存储中检索保存的帐户和密码信息，使用密钥和盐对其进行解密，然后使用检索到的信息更新状态。该函数在组件的设置阶段被调用，以确保用户之前保存的登录信息被预先填写在登录表单中。 */
const decodeAccountAndPassword = () => {
  const encoded = window.localStorage.getItem(LOGIN_USER_ACCOUNT_INFO);
  const ciphertext = encoded?.replace("/" + SALT_FOR_ACCOUNT, "");
  if (ciphertext) {
    try {
      const decoded = CryptoJS.AES.decrypt(ciphertext, KEY_FOR_ACCOUNT);
      const json = JSON.parse(decoded.toString(CryptoJS.enc.Utf8));
      // dispatch({
      //   type: "user_info_fill_out",
      //   username: json.username,
      //   phoneNumber: json.phoneNumber,
      //   password: json.password,
      // });
      return json;
    } catch (error) {
      console.error(error);
    }
  }
};

export interface PageState {
  mode?: number;
  verifyCode: string;
  verifyUniqid: string;
  // 表单数据
  username: string;
  password: string;
  imageCode: string;
  // 顶象验证
  dxToken: string;
  smsCode: string;
  phoneNumber: string;
}
const loginInfo = decodeAccountAndPassword();
const pagestate = {
  mode: 0,
  verifyCode: "",
  verifyUniqid: "",
  username: loginInfo?.username,
  password: loginInfo?.password,
  imageCode: "",
  dxToken: "",
  smsCode: "",
  phoneNumber: loginInfo?.phoneNumber,
  verifyToken: "",
};

type ActionTypes =
  | "set_code_base64_img"
  | "user_info_fill_out"
  | "update_username"
  | "update_password"
  | "update_imageCode"
  | "update_mode"
  | "update_phoneNumber"
  | "update_smsCode"
  | "login_failed"
  | "set_dx_token";

export const stateReducer = (
  state: PageState,
  action: StateActions<PageState, ActionTypes>
): PageState => {
  switch (action.type) {
    case "set_code_base64_img":
      return Object.assign({}, state, {
        verifyCode: action?.verifyCode,
        verifyUniqid: action?.verifyUniqid,
      });
    case "user_info_fill_out":
      return Object.assign({}, state, {
        username: action?.username,
        password: action?.password,
      });
    case "update_username":
      return Object.assign({}, state, {
        username: action?.username,
      });
    case "update_password":
      return Object.assign({}, state, {
        password: action?.password,
      });
    case "update_imageCode":
      return Object.assign({}, state, {
        imageCode: action?.imageCode,
      });
    case "update_mode":
      return Object.assign({}, state, {
        mode: action?.mode,
      });
    case "update_phoneNumber":
      return Object.assign({}, state, {
        phoneNumber: action?.phoneNumber,
      });
    case "update_smsCode":
      return Object.assign({}, state, {
        smsCode: action?.smsCode,
      });
    case "login_failed":
      return Object.assign({}, state, {
        verifyToken: "",
        imageCode: "",
        smsCode: "",
      });
    case "set_dx_token":
      return Object.assign({}, state, {
        dxToken: action?.dxToken,
      });
    default:
      return state;
  }
};

// 初始化
export const useSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const handler = useRef<ToastHandler>()
  const [dxObj, setDxObj] = useState<any>(undefined);

  const { config } = useConfigContext();
  const { login: loginAuth } = useUserAuthContext();

  const [rememberPassword, setRememberedPassword] = useLocalStorage(
    LOGIN_REMEMBER_PASSWORD,
    false
  )(selectorLocalStorage);

  const [, setToken] = useLocalStorage(USER_TOKEN, "")(selectorLocalStorage);

  const [state, dispatch] = useCreateReducer<PageState, ActionTypes>(
    stateReducer,
    pagestate,
    {
      mode: Number(config?.userRegisterType || 2),
    }
  );

  const [verifySecurePassword] = useGlobalVerify();

  const flushCodeImage = async () => {
    const response = await getImageCode({ codeType: "login" });
    if (response.ok) {
      dispatch({
        type: "set_code_base64_img",
        verifyCode: response.data?.verifyCode,
        verifyUniqid: response.data?.verifyUniqid,
      });
    } else {
      // TODO: 请求失败的处理
    }
  };

  const flushSMSCode = async () => {
    const isPhoneNumberValid = validatePhoneNumber(state.phoneNumber);
    if (isPhoneNumberValid) {
      Toast.show(isPhoneNumberValid);
      return Promise.resolve(false);
    }
    const response = await getSMSCode({
      phoneNumber: state.phoneNumber,
      codeType: "login",
    });
    Toast.show(response.msg);
    return response.ok;
  };

  const loginWithPassword = async () => {
    const isUsernameValid = validateUserName(state.username);
    const isPasswordValid = validatePassword(state.password);
    
    let isImageCodeValid = undefined;
    let isverifyTokenValid = undefined;

    if (isSlideCodeType && !state.dxToken) {
      isverifyTokenValid = "请滑动验证";
    }
    if (isGraphicsCodeType) {
      isImageCodeValid = validateImageCode(state.imageCode);
    }


    if (isUsernameValid || isPasswordValid || isImageCodeValid || isverifyTokenValid) {
      return Toast.show({
        content: isUsernameValid || isPasswordValid || isImageCodeValid || isverifyTokenValid,
      });
    }
    const response = await login({
      userName: state.username,
      password: state.password,
      code: state.imageCode,
      verifyUniqid: state.verifyUniqid,
      verifyToken: state.dxToken,
    });
    handleLoginReponse(response);
  };

  const loginWithPhoneNumber = async () => {
    const isPhoneNumberValid = validatePhoneNumber(state.phoneNumber);
    const isSMSCodeValid = validateSMSCode(state.smsCode);

    let isImageCodeValid = undefined;
    let isverifyTokenValid = undefined;
    if (isSlideCodeType && !state.dxToken) {
      isverifyTokenValid = "请滑动验证";
    }
    if (isGraphicsCodeType) {
      isImageCodeValid = validateImageCode(state.imageCode);
    }

    if (isPhoneNumberValid || isSMSCodeValid || isImageCodeValid || isverifyTokenValid) {
      return Toast.show({
        content: isPhoneNumberValid || isSMSCodeValid || isImageCodeValid || isverifyTokenValid,
      });
    }
    const response = await loginSMS({
      phoneNumber: state.phoneNumber,
      smsCode: state.smsCode,
      code: state.imageCode,
      verifyUniqid: state.verifyUniqid,
      verifyToken: state.dxToken,
    });
    handleLoginReponse(response);
  };

  const loginAutomatically = async () => {
    const auth = window.localStorage[LINK_ACCOUNT_TOKEN];
    const autoRegister = searchParams.get('auto_register')
    if(auth && autoRegister == '1') {
      handler.current = Toast.show({
        icon: "loading",
        content: "自动注册中...",
        duration: 0,
      })
      const r = await postAuthorizeRegister({authToken: auth})
      handler.current.close()
      if(r.ok) {
        handleLoginReponse(r)
      }
      else {
        Toast.show(r.msg)
      }
    }
  }

  const loginWithUnbind = async () => {
    let isUsernameValid = undefined;
    let isPasswordValid = undefined;
    let isPhoneNumberValid = undefined;
    let isSMSCodeValid = undefined;

    if( config?.loginDeviceType === '1' ){//用户名密码
      isUsernameValid = validateUserName(state.username);
      isPasswordValid = validatePassword(state.password);
    }else if( config?.loginDeviceType === '2' ){//手机号短信验证
      isPhoneNumberValid = validatePhoneNumber(state.phoneNumber);
      isSMSCodeValid = validateSMSCode(state.smsCode);
    }

    let isImageCodeValid = undefined;
    let isverifyTokenValid = undefined;
    if (isSlideCodeType && !state.dxToken) {
      isverifyTokenValid = "请滑动验证";
    }
    if (isGraphicsCodeType) {
      isImageCodeValid = validateImageCode(state.imageCode);
    }

    if (isUsernameValid || isPasswordValid || isPhoneNumberValid || isSMSCodeValid || isImageCodeValid || isverifyTokenValid) {
      return Toast.show({
        content: isUsernameValid || isPasswordValid || isPhoneNumberValid || isSMSCodeValid || isImageCodeValid || isverifyTokenValid,
      });
    }


    const securePassword = await verifySecurePassword();

    if (securePassword) {
      const fn = isUserCheck ? checkDevice : loginDevice;
      const params:LoginDeviceParams = {
        code: state.imageCode,
        verifyUniqid: state.verifyUniqid,
        verifyToken: state.dxToken,
        securePassword: securePassword as string,
      };
      if( config?.loginDeviceType === '1' ){//用户名密码
        params.userName = state.username;
        params.password = state.password;
      }else if( config?.loginDeviceType === '2' ){//手机号短信验证
        params.phoneNumber = state.phoneNumber;
        params.smsCode = state.smsCode;
      }
      const response = await fn(params);
      if( isUserCheck ){
        Toast.show(response.msg);// 提示信息
        if (response.ok)    {
           navigate("/");//用户设备校验 校验成功返回首页
        }
        return;
      }
      handleLoginReponse(response);
    }
  };

  /* `const saveAccountAndPassword`是一个将用户的账号密码信息保存到本地存储的函数。它首先使用用户的用户名、电话号码和密码创建一个 JSON 对象，然后使用
  `KEY_FOR_ACCOUNT` 密钥加密对象并将其保存到本地存储。加密数据在保存到本地存储之前也会用 SALT_FOR_ACCOUNT 加盐。 */
  const saveAccountAndPassword = () => {
    const json = JSON.stringify({
      username: state.username,
      phoneNumber: state.phoneNumber,
      password: state.password,
    });
    const encoded = CryptoJS.AES.encrypt(json, KEY_FOR_ACCOUNT);
    // 加密数据加盐储存在本地
    window.localStorage.setItem(
      LOGIN_USER_ACCOUNT_INFO,
      `${encoded.toString()}/${SALT_FOR_ACCOUNT}`
    );
  };

  const handleLoginReponse = (
    response: RequestResponse<APIUserMyAccountLoginSMS | APIUserMyAccountLogin>
  ) => {
    if (response.code === 1001) {
      navigate("/qr_login");
      return;
    }

    if (response.ok) {
      // 记住密码时需要保存密码
      rememberPassword && saveAccountAndPassword();
      // 储存到 localStorage
      setToken(response.data?.token as string);
      // 设置请求头token
      setBusinessAxiosToken(response.data?.token as string);
      // 登录并跳转到首页
      loginAuth(response.data?.userInfo as UserInfo["response"]);
    } else {
      // 刷新图形验证码h
      flushCodeImage();
      // TODO: 如果顶象验证开启需要刷新顶象
      console.log('dxObj==',dxObj);
      dxObj && dxObj.reload();
      // 提示错误信息
      Toast.show(response.msg);
      // 密码登录失败 重置参数
      dispatch({ type: "login_failed" });
    }
  };

  const submit = () => {
    // TODO: 如果开启了顶象验证这里要判断是否通过顶象验证 dxToken
    // config?.verifyCodeType === 1 顶象验证，默认是图片验证码
    if (isUnbindMode || isUserCheck) return loginWithUnbind();
    if (isPasswordLoginMode) return loginWithPassword();
    if (isSmsLoginMode) return loginWithPhoneNumber();
  };

  useEffect(() => {
    loginAutomatically();
    flushCodeImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    let dx_token = document.getElementById('dx_token');
    if(dx_token){
        insertDx((_dx) => {
          const dx_obj = _dx.Captcha(document.getElementById('dx_token'), {
            appId: process.env.REACT_APP_DX_APPID, //appId，在控制台应用管理或应用配置模块获取
            apiServer: process.env.REACT_APP_DX_API_SERVER,
            style: "inline",
            success: (token:string) => {
              // 获取验证码token，用于后端校验，注意获取token若是sl开头的字符串，则是前端网络不通生成的降级token,请检查前端网络及apiServer地址。
              console.log('dxToken==',token);
              dispatch({
                type: "set_dx_token",
                dxToken: token,
              });
            }
          });
          setDxObj(dx_obj);
        });
    }
  }, []);

  // 登录模式
  let isPasswordLoginMode = state.mode === 1;
  let isSmsLoginMode = state.mode !== 1;
  const contentOfSwitchLoginModeButton = isPasswordLoginMode
    ? "短信验证码登录"
    : isSmsLoginMode
    ? "密码登录"
    : "错误";
  const location = useLocation();  // 获取当前路由路径
  // const isUnbindMode = window.location.pathname === "/unbind";
  const isUnbindMode = location.pathname === "/unbind";
  const isUserCheck = location.pathname === "/user_check";//用户设备校验页面
  const isGraphicsCodeType = config?.verifyCodeType === "1";
  const isSlideCodeType = config?.verifyCodeType === "2";

  //unbind 清除登录设备页面 或者 user_check 校验登录设备页面 根据 loginDeviceType 1 用户名密码/2手机号 修改mode模式展示 手机号/密码验证
  if( isUnbindMode || isUserCheck ){
    isPasswordLoginMode = (config?.loginDeviceType || "1") === "1";
    isSmsLoginMode = (config?.loginDeviceType || "1") === "2";
  }

  console.log('useLocation==',location.pathname);



  return {
    state,
    dispatch,
    flushCodeImage,
    flushSMSCode,
    isPasswordLoginMode,
    isSmsLoginMode,
    contentOfSwitchLoginModeButton,
    isUnbindMode,
    isUserCheck,
    rememberPassword,
    setRememberedPassword,
    submit,
    version: config?.appVersion,
    isGraphicsCodeType,
    isSlideCodeType,
  };
};
